import { ExecutionRunDetailsPanels } from '@/features/executionRuns/components/ExecutionRunDetailsPanels';
import { ExecutionRunStatusRows } from '@/features/executionRuns/components/ExecutionRunStatusRows';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { ResultsFileSuiteExecutionPanelsSection } from '@/features/uploads/components/ResultsFileSuiteExecutionPanelsSection';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { Stack } from '@/ui/layout/Stack';
import { Chip, Text, formatStatusLabel, resolveStatusChipTone } from '@/ui/primitives';
import type { BuildDto, ExecutionRunDto, SuiteExecutionDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ExecutionRunDetailContentProps = {
  executionRun: ExecutionRunDto;
  build: BuildDto | null;
  suiteExecutions: SuiteExecutionDto[];
  suiteExecutionsLoading?: boolean;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const ExecutionRunDetailContent = ({
  executionRun,
  build,
  suiteExecutions,
  suiteExecutionsLoading = false,
  onDelete,
  deleteLoading = false,
}: ExecutionRunDetailContentProps): ReactElement => {
  const hasDuration = executionRun.durationMs !== null && executionRun.durationMs !== undefined;
  const durationLabel = hasDuration ? formatDuration(executionRun.durationMs) : null;
  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Execution Run" />
          {executionRun.id}
        </Text>
      }
      status={
        <Chip
          label={formatStatusLabel(executionRun.status)}
          tone={resolveStatusChipTone(executionRun.status)}
          transform="uppercase"
          size="small"
        />
      }
      headerMeta={
        durationLabel ? (
          <Text role="bodyMuted" component="span" sx={{ whiteSpace: 'nowrap' }}>
            {durationLabel}
          </Text>
        ) : null
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Execution Run"
          dialogDescription={
            <Stack gap="sm">
              <Text role="body" color="textSecondary">
                Deleting will remove any associated suite executions and test executions.
              </Text>
              <Text role="body" color="textSecondary">
                This action cannot be undone.
              </Text>
            </Stack>
          }
          confirmLabel="Delete Execution Run"
        />
      }
      metadata={<ExecutionRunDetailsPanels executionRun={executionRun} build={build} />}
      summary={<ExecutionRunStatusRows executionRun={executionRun} />}
      primaryContent={
        <ResultsFileSuiteExecutionPanelsSection
          suiteExecutions={suiteExecutions}
          loading={suiteExecutionsLoading}
          executionRunId={executionRun.id}
          executionRun={executionRun}
          showExecutionRunSummary={false}
          showSuitesHeading
        />
      }
    />
  );
};
